<template>
  <ag-index-page
    :view="view"
    :columns="columns"
    create-to="/admin/certificates/new"
    create-label="Importar certificado"
    create-icon="mdi-file-import"
    remove-title="Eliminar certificado"
  />
</template>

<script setup lang="ts">
import type { ThinCertificate } from "bancojs";
import { banco } from "@/api/client";
import { useIndexView } from "@/composables/useIndexView";
import AgIndexPage from "@/components/AgIndexPage.vue";
import type { AgDataTableOptions } from "@/components/table/types";
import { mode2FALabel, stateLabel } from "@/utils/labels";

const view = useIndexView<
  ThinCertificate,
  "fingerprint" | "key_alias" | "key_id" | "csr_id" | "user_username" | "person_dni" | "twofa_mode"
>({
  title: "Índice de certificados",
  rowKey: "fingerprint",
  defaultSort: "fingerprint,asc",
  fetchPage: (params) => banco.admin.certificates.find(params),
  remove: (item) => banco.admin.certificates.destroy(item.fingerprint),
  removedMessage: (item) => `Se eliminó el certificado ${item.fingerprint.slice(0, 12)}…`,
  filters: [
    { field: "fingerprint", label: "Fingerprint" },
    { field: "key_alias", label: "Alias llave" },
    { field: "key_id", label: "ID llave" },
    { field: "csr_id", label: "ID CSR" },
    { field: "user_username", label: "Usuario" },
    // Filtros disponibles en la API que la vista anterior no ofrecía.
    { field: "person_dni", label: "RUT del titular" },
    { field: "twofa_mode", label: "Modo 2FA" },
  ],
  confirm: {
    // El formulario anterior pedía escribir la palabra "eliminar", igual para
    // todos los certificados. Pedir el fingerprint obliga a mirar CUÁL se está
    // borrando, que es el punto de una confirmación escrita.
    expected: (item) => item.fingerprint,
    label: "Fingerprint",
    hint: "Para confirmar ingresa el <b>fingerprint</b> del certificado a eliminar.",
    error: "El fingerprint no es correcto.",
    warning: (item) =>
      `Esta acción eliminará el certificado ${item.fingerprint} y sus permisos de firma.`,
  },
});

const columns: AgDataTableOptions[] = [
  { header: { text: "#", value: "$index", width: 60, sortable: false }, row: { type: "INDEX" } },
  {
    header: { text: "Fingerprint", value: "fingerprint", class: "text-no-wrap" },
    row: {
      type: "LINK",
      to: "/admin/certificates/{fingerprint}/edit",
      // El fingerprint completo son 40 caracteres y desborda la columna.
      transform: (val) => `${String(val).slice(0, 16)}…`,
    },
  },
  {
    header: { text: "Dueño", value: "user_id", class: "text-no-wrap" },
    row: { type: "LINK", to: "/admin/users/{user_id}/edit" },
  },
  {
    header: { text: "Llave", value: "key_id", class: "text-no-wrap" },
    row: { type: "LINK", to: "/admin/keys/{key_id}/edit" },
  },
  {
    header: { text: "Estado", value: "state", class: "text-no-wrap" },
    row: {
      type: "CHIP",
      label: true,
      color: { ACTIVE: "success", INACTIVE: "error", DELETED: "grey" },
      transform: (val) => stateLabel(val),
    },
  },
  {
    // Expuesto por la API desde ago-2025 y que el GUI no mostraba.
    header: { text: "2FA", value: "twofa_mode", class: "text-no-wrap" },
    row: {
      type: "CHIP",
      label: true,
      color: { NONE: "grey", TOTP: "indigo", RSAOTP: "indigo", SMS: "blue", EMAIL: "blue" },
      transform: (val) => mode2FALabel(val),
    },
  },
  {
    header: { text: "", value: "$options", class: "text-no-wrap", sortable: false },
    row: {
      type: "OPTIONS",
      options: [
        { label: "Ver detalle", icon: "mdi-eye", link: "/admin/certificates/{fingerprint}/edit" },
        {
          label: "Eliminar",
          icon: "mdi-delete",
          action: (item) => view.openRemove(item as ThinCertificate),
        },
      ],
    },
  },
];
</script>
